/**
 * 房间选择
 */
import React, { useMemo } from 'react';
import { Select } from 'antd';
import intl from 'react-intl-universal';

import ReserveRoomItem from '@/components/Reservation/RoomItem';
import { RoomCategory } from '@/redux/reducers/reservation/base.d';
import {
  findItemByKey,
  getRoomCheckInStatus,
  getRoomReserveExchangeStatus,
} from '@/redux/actions/reservation/helper';
import { RoomSymbol } from '@/redux/reducers/reservation/reserveExchange';
import { RoomCleanStatus, EnableBookEnum } from '@/config/constant';

import styles from './index.module.scss';

const { Option } = Select;

interface ITypeProps {
  allRoomList: RoomCategory[];
  value: number; // 房型id
  onChange: (value: number) => void;
  getPopContainer: () => HTMLElement;
}

// 房型选择
const TypeSelector: React.FC<ITypeProps> = (props) => {
  const { allRoomList, getPopContainer, value, onChange } = props;

  const selectOptions = useMemo(() => {
    return (allRoomList || []).map((item) => {
      const { id, name, roomList } = item;
      const roomCount = (roomList || []).length;
      // 预订都可选 入住不可选脏、住房
      // v2.21 subRoomCount 等于可订房间数
      const avaliableRoomList = (roomList || []).filter((e) => getRoomCheckInStatus(e));
      return (
        <Option value={id} label={name} key={id} disabled={!roomCount}>
          <div className={styles.optionItem}>
            <div className={styles.optionLabel}>{name}</div>
            <div>
              {avaliableRoomList?.length || 0}/{roomCount}
            </div>
          </div>
        </Option>
      );
    });
  }, [allRoomList]);

  return (
    <Select
      placeholder={intl.get('reservation_field_select_default_placeholder')}
      notFoundContent={intl.get('drawer_exchange_room_type_null_content')}
      className={styles.roomTypeSelector}
      popupClassName={styles.roomDropdownWrapper}
      value={value}
      getPopupContainer={getPopContainer}
      onChange={onChange}
    >
      {selectOptions}
    </Select>
  );
};

interface IRoomProps {
  allRoomList: RoomCategory[];
  value: number; // 房间id
  typeId: number; // 房型id
  onChange: (value: number) => void;
  isReserve: boolean; // 是否预订
  originRoomId: number; // 原房间id
  getPopContainer: () => HTMLElement;
}

const RoomSelector: React.FC<IRoomProps> = (props) => {
  const { allRoomList, getPopContainer, typeId, value, onChange, isReserve, originRoomId } = props;

  const selectOptions = useMemo(() => {
    const category = findItemByKey(allRoomList, 'id', typeId);
    const result = [];
    if (isReserve) {
      const dummyRoomData = {
        roomNo: intl.get('app_reservation_room_queue_no'),
        mainStatus: RoomSymbol.Block as any,
        cleanStatus: RoomCleanStatus.Clean,
        roomTypeId: null,
        id: null,
      };
      result.push(
        <Option
          value={RoomSymbol.Block}
          label={intl.get('app_reservation_room_queue_no')}
          key={RoomSymbol.Block}
        >
          <ReserveRoomItem data={dummyRoomData} showCalendar={false} />
        </Option>,
      );
    }
    if (category && category.roomList) {
      (category.roomList || []).forEach((item) => {
        const { id, roomNo } = item;
        const isOriginRoom = id === originRoomId; // 是否是“原”房间
        // v2.21 预订换排房：“停” 满”  “原”不可选 入住换排房：不可选“停” 满”  “原” “脏” “住”(以前预订换排房都可选；入住换排房不可选“脏” “住”)
        const itemDisabled = isReserve
          ? !getRoomReserveExchangeStatus(item, isOriginRoom)
          : !getRoomCheckInStatus(item) && !isOriginRoom;

        result.push(
          <Option value={id} label={roomNo} key={id} disabled={itemDisabled}>
            <ReserveRoomItem data={item} showCalendar={false} showOriginTag={isOriginRoom} />
          </Option>,
        );
      });
    }
    return result;
  }, [allRoomList, typeId, isReserve]);

  return (
    <Select
      placeholder={intl.get('reservation_field_select_default_placeholder')}
      notFoundContent={intl.get('drawer_exchange_room_no_null_content')}
      className={styles.roomNoSelector}
      popupClassName={styles.roomDropdownWrapper}
      value={value}
      getPopupContainer={getPopContainer}
      onChange={onChange}
      optionLabelProp="label"
    >
      {selectOptions}
    </Select>
  );
};

export const RoomTypeSelector = TypeSelector;
export const RoomNoSelector = RoomSelector;
